/*
  Load More pagination for the product grids (collection and search).

  Two jobs:

  1. Append the next batch of products in place, keeping the button, the
     "showing x of y" count and the grid in step.

  2. Remember how far the shopper got. Shopify renders one page per request,
     so following a product link and coming back would otherwise drop them at
     the top of the first batch, having lost everything they had loaded. The
     depth and their place in the grid are kept per URL in sessionStorage and
     replayed when they return.

  Restoring re-fetches those pages rather than caching their markup. Prices,
  stock and badges come from NetSuite and go stale quickly, and a full grid of
  cached HTML would not reliably fit in sessionStorage anyway.
*/

const PAGINATION_MAX_RESTORE_PAGES = 10;

const GridPosition = {
  prefix: 'swd:grid-position:',
  ttl: 30 * 60 * 1000,
  watching: false,

  // Filters and sort belong in the key — a different set of filters is a
  // different grid, and should not inherit another one's position. The page
  // number does not: it is what we are reconstructing.
  key() {
    const url = new URL(window.location.href);

    url.searchParams.delete('page');

    return this.prefix + url.pathname + url.search;
  },

  read() {
    try {
      const stored = sessionStorage.getItem(this.key());

      if (!stored) return null;

      const state = JSON.parse(stored);

      return state && Date.now() - state.at < this.ttl ? state : null;
    } catch (error) {
      return null;
    }
  },

  write(patch) {
    const state = Object.assign({ pages: 1, scrollY: 0, anchor: null }, this.read(), patch, { at: Date.now() });

    try {
      sessionStorage.setItem(this.key(), JSON.stringify(state));
    } catch (error) {
      // Private browsing, or a full quota. The grid still works, it just
      // forgets where the shopper was.
    }

    return state;
  },

  clear() {
    try {
      sessionStorage.removeItem(this.key());
    } catch (error) {
      // Nothing stored, nothing to drop.
    }
  },

  // The product they clicked beats a pixel offset as an anchor: the grid can
  // reflow between visits, and the card is what they were actually looking at.
  watch() {
    if (this.watching) return;
    this.watching = true;

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;

        if (!target || typeof target.closest !== 'function') return;

        const item = target.closest('.js-product-grid .grid__item');

        if (!item) return;

        const link = item.querySelector('a[href*="/products/"]');

        this.write({
          scrollY: Math.round(window.scrollY),
          anchor: link ? new URL(link.href, window.location.origin).pathname : null,
        });
      },
      true
    );

    window.addEventListener('pagehide', () => this.write({ scrollY: Math.round(window.scrollY) }));
  },
};

class Pagination extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.grid = document.querySelector('.js-product-grid');

    if (!this.grid) return;

    this.currentPage = Number(this.dataset.currentPage) || 1;
    this.nextPage = Number(this.dataset.nextPage);
    this.totalPages = Number(this.dataset.total);
    this.triggerType = this.dataset.triggerType;
    this.trigger = this.querySelector('.js-loader');
    this.loading = false;

    if (this.trigger) {
      this.triggerType === 'button' ? this.addClickListener() : this.addObserver();
    }

    // Only the element the document loaded with may restore. Every later
    // instance arrives from a Load More or a filter change, where the shopper
    // is already looking at the grid and moving them would be an ambush.
    if (Pagination.hydrated) return;
    Pagination.hydrated = true;

    GridPosition.watch();
    this.restore();
  }

  addClickListener() {
    // event.detail is 0 when the button was activated from the keyboard. The
    // replacement button only takes focus in that case, so a mouse click does
    // not leave a focus ring behind.
    this.trigger.addEventListener('click', (event) => this.loadNextPage(event.detail === 0));
  }

  addObserver() {
    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && this.loadNextPage(false)),
      { root: null, rootMargin: '400px', threshold: 0 }
    );

    this.observer.observe(this.trigger);
  }

  async loadNextPage(focusTrigger) {
    if (this.loading || this.nextPage > this.totalPages) return;

    this.setLoading(true);

    try {
      const page = await this.fetchPage(this.nextPage);

      this.appendItems(page);

      // The section id travels with the position so the head snippet can build
      // the restore request before the grid — or this script — exists.
      GridPosition.write({
        pages: this.nextPage,
        scrollY: Math.round(window.scrollY),
        section: this.dataset.paginationSection,
      });

      // From here the page is longer than anything the browser saved for it,
      // so its own scroll restoration would land short. We take it over.
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

      this.replaceSelf(page, focusTrigger);
    } catch (error) {
      // Leave the button where it is so it can simply be pressed again.
      this.setLoading(false);
      console.error('Load more failed', error);
    }
  }

  async fetchPage(page) {
    const url = new URL(window.location.href);

    url.searchParams.set('page', page);
    url.searchParams.set('section_id', this.dataset.paginationSection);

    const response = await fetch(url.toString());

    if (!response.ok) throw new Error(`Pagination request failed: ${response.status}`);

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  appendItems(page) {
    const fragment = document.createDocumentFragment();

    // CMS page tiles come through with the rest of the batch. Each tile is now
    // rendered on the one page its position falls in, so a tile arrives exactly
    // once and already sits between the right two products — there is nothing
    // to de-duplicate and nothing to reposition. Previously the single promo
    // card was rendered into every page and had to be skipped here.
    page.querySelectorAll('.js-product-grid > .grid__item').forEach((item) => {
      fragment.appendChild(item);
    });

    this.grid.appendChild(fragment);
  }

  // The fetched page carries its own count and button state, so swapping the
  // whole element in is what keeps "showing 96 of 259" honest and drops the
  // button on the last page.
  replaceSelf(page, focusTrigger) {
    const replacement = page.querySelector('.js-pagination');

    if (!replacement) {
      this.remove();
      return;
    }

    this.replaceWith(replacement);

    if (!focusTrigger) return;

    // On the last batch there is no button left to focus, so the count takes
    // it instead — it is the thing that just changed.
    const focusTarget = replacement.querySelector('.js-loader') || replacement.querySelector('.pagination__count');

    if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
  }

  // Only a step back through history counts as returning to a grid. Picking
  // the category again from the menu is a fresh visit and starts at the first
  // batch, which is what shoppers expect. A reload is the same page over
  // again, so it keeps its place.
  static isReturnVisit() {
    const entry = typeof performance.getEntriesByType === 'function' && performance.getEntriesByType('navigation')[0];

    if (entry) return entry.type === 'back_forward' || entry.type === 'reload';

    // Navigation Timing Level 1: 2 is back/forward, 1 is a reload.
    return Boolean(performance.navigation) && [1, 2].includes(performance.navigation.type);
  }

  // Whether this load is a return to an expanded grid, and to what depth.
  // The inline script in snippets/product-grid.liquid asks the same questions
  // before the cards are parsed, so it can hide the grid ahead of the first
  // paint — change the two together.
  stateToRestore() {
    // An explicit ?page= is someone's own link into the middle of the grid.
    // Stacking a remembered depth on top of it would show a gap.
    if (new URL(window.location.href).searchParams.has('page')) return null;

    // Dropped rather than just ignored: left in place, the depth would be
    // picked up again by the next product click and replayed on the way back,
    // showing batches this visit never opened.
    if (!Pagination.isReturnVisit()) {
      GridPosition.clear();
      return null;
    }

    const state = GridPosition.read();

    return state && state.pages > 1 ? state : null;
  }

  async restore() {
    const state = this.stateToRestore();

    if (!state) {
      Pagination.finishRestoring();
      return;
    }

    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    const lastPage = Math.min(state.pages, this.totalPages, PAGINATION_MAX_RESTORE_PAGES);
    const pages = [];

    for (let page = this.currentPage + 1; page <= lastPage; page += 1) pages.push(page);

    if (!pages.length) {
      this.settle(state);
      return;
    }

    this.setLoading(true);

    // snippets/grid-restore starts these from <head>, long before this script
    // is allowed to run, so by now they are usually already back. Falls back to
    // fetching here if the head could not — an older stored position with no
    // section id, or a page count that has since changed.
    const prefetched = window.SWDGridRestoreFetches;
    const fetched =
      prefetched && prefetched.length === pages.length
        ? (await Promise.all(prefetched)).map((html) =>
            html ? new DOMParser().parseFromString(html, 'text/html') : null
          )
        : await Promise.all(pages.map((page) => this.fetchPage(page).catch(() => null)));

    let last = null;

    for (const page of fetched) {
      // Stop at the first page that failed rather than appending across the
      // gap, which would leave the count claiming products that are not there.
      if (!page) break;

      this.appendItems(page);
      last = page;
    }

    this.setLoading(false);

    if (last) this.replaceSelf(last, false);

    this.settle(state);
  }

  // Put the shopper back in place, let the images in front of them catch up,
  // then uncover.
  //
  // Uncovering is deliberately not deferred to requestAnimationFrame. Browsers
  // pause rAF in a backgrounded tab, so a shopper who opened the product in a
  // background tab, or switched away while it loaded, came back to a page that
  // stayed covered until the 4s failsafe — measured at 11s in testing. The
  // scroll above is applied synchronously, so there is nothing left to wait for.
  async settle(state) {
    this.scrollToSavedPosition(state);

    await this.waitForVisibleImages();

    Pagination.finishRestoring();
  }

  // Reveals the grid, whether or not anything was restored. The inline script
  // that hides it also clears itself on a timer, so a failure here costs a
  // pause rather than an invisible collection.
  static finishRestoring() {
    document.documentElement.classList.remove('grid-restoring');

    // The moment the shopper gets the grid back. Left in deliberately: it is
    // the only number that describes this journey, and it is free to read from
    // the console or an RUM tool if the return ever feels slow again.
    if (performance.mark) performance.mark('swd:grid-restored');
  }

  // The batch that lands in view arrives with its images still to load, so
  // uncovering immediately shows a grid of empty frames that then fill in.
  // Capped, so one slow image can never hold the page back.
  waitForVisibleImages(cap = 400) {
    const pending = Array.from(this.grid.querySelectorAll('img')).filter((image) => {
      if (image.complete) return false;

      const box = image.getBoundingClientRect();

      return box.bottom > 0 && box.top < window.innerHeight;
    });

    if (!pending.length) return Promise.resolve();

    const loaded = pending.map(
      (image) =>
        new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        })
    );

    return Promise.race([Promise.all(loaded), new Promise((resolve) => setTimeout(resolve, cap))]);
  }

  // The saved offset is exact whenever the grid comes back the same shape,
  // which is the ordinary case, so it is preferred — re-centring on the card
  // would move a product the shopper had at the top of their screen. The card
  // is the rescue for when the grid has reflowed since they left.
  scrollToSavedPosition(state) {
    if (state.scrollY) window.scrollTo(0, state.scrollY);

    const link = state.anchor ? this.grid.querySelector(`a[href^="${state.anchor}"]`) : null;
    const item = link ? link.closest('.grid__item') : null;

    if (!item) return;

    const box = item.getBoundingClientRect();

    if (box.bottom < 0 || box.top > window.innerHeight) item.scrollIntoView({ block: 'center' });
  }

  setLoading(loading) {
    this.loading = loading;

    if (this.trigger) this.trigger.classList.toggle('loading', loading);

    this.grid.setAttribute('aria-busy', String(loading));
  }
}

customElements.define('infinite-pagination', Pagination);

// A collection short enough not to paginate has no <infinite-pagination> to
// hang the listeners off, but the shopper's place in it should still be
// remembered. Loaded async, this can now run before the grid has been parsed,
// so it waits for the document when it has to. Safe either way — watch() guards
// against binding twice.
(function watchGrid() {
  if (document.querySelector('.js-product-grid')) return GridPosition.watch();

  document.addEventListener('DOMContentLoaded', () => GridPosition.watch(), { once: true });
})();
